import { refreshMultipleChoiceOrder, getBringInPayloadSlots } from '../utils/refreshMultipleChoiceOrder'
// hooks/useSurveyAnswerConverter.js
import { useCallback, useRef } from 'react'
import axios from 'axios'
import {
  recordProgressEvent,
  withProgressDiagnostics,
} from '../utils/progressDiagnostics'
import {
  validateAnswerPayload,
  formatViolationMessage,
  buildViolationDiagnostics,
} from '../utils/validateAnswerPayload'
import { findBringInSourceName } from '../utils/bringInSlotIdentity'

export const useSurveyAnswerConverter = ({
  pid,
  ac,
  tk,
  choiceIndexMapRef,
  choicesOrderRef,
  bringOptionsMapRef,
  otherAnswers,
  noneAnswers,
  matrixAnswers,
  timestamps,
  survey,
}) => {
  const host = process.env.REACT_APP_HOST
  const SaveAnswersUrl = '/clientWeb/v1/SaveAnswers'

  // discord 通知
  const sendDiscordNotification = async (message) => {
    try {
      await axios.post('/api/send-notification', {
        content: withProgressDiagnostics(message, survey),
      })
    } catch (error) {
      console.error('Failed', error)
    }
  }

  // 轉換答案為提交格式（簡化版，不包含檔案處理，用於進度儲存）
  const convertAnswersSimple = useCallback(
    (surveyData) => {
      console.log('🔄 convertAnswersSimple 收到的 surveyData:', surveyData)

      // 第一步：預處理，確保所有 other 和 none 的索引都正確設置
      Object.entries(surveyData).forEach(([key, value]) => {
        if (!key.includes('-Comment')) {
          // 檢查是否有對應的問題和 other/none 選項
          const question = survey?.getQuestionByName?.(key)
          if (question) {
            // 處理 other 選項索引
            if (question.showOtherItem && !otherAnswers[key]) {
              // 檢查值中是否包含 'other'
              const hasOtherValue = Array.isArray(value)
                ? value.includes('other')
                : value === 'other'

              if (hasOtherValue) {
                const otherIndex =
                  question.choices.length + (question.showNoneItem ? 1 : 0)
                otherAnswers[key] = { otherIndex: otherIndex }
                console.log(
                  `🔧 預處理時自動設定 ${key} 的 other 索引:`,
                  otherIndex
                )
              }
            }

            // 處理 none 選項索引
            if (question.showNoneItem && !noneAnswers[key]) {
              const hasNoneValue = Array.isArray(value)
                ? value.includes('none')
                : value === 'none'

              if (hasNoneValue) {
                const noneIndex = question.choices.length
                noneAnswers[key] = { noneIndex: noneIndex }
                console.log(
                  `🔧 預處理時自動設定 ${key} 的 none 索引:`,
                  noneIndex
                )
              }
            }
          }
        }
      })

      console.log('🔍 預處理後的 otherAnswers:', otherAnswers)
      console.log('🔍 預處理後的 noneAnswers:', noneAnswers)

      const transformedData = Object.entries(surveyData).reduce(
        (acc, [key, value]) => {
          console.log(`📝 處理問題 ${key}:`, value)

          // 處理評論問題（-Comment 結尾的）
          if (key.includes('-Comment')) {
            const actualKey = key.split('-')[0]
            console.log(`💬 處理評論問題 ${key}, 對應主問題 ${actualKey}`)

            let correspondingObj = acc.find(
              (obj) => obj.qi === Number(actualKey.slice(1))
            )

            if (correspondingObj) {
              console.log('📦 找到對應物件:', correspondingObj)

              // 獲取 other 索引，如果沒有則計算
              let otherKey = otherAnswers[actualKey]?.otherIndex

              if (otherKey === undefined) {
                console.log(
                  `⚠️ otherAnswers 中找不到 ${actualKey} 的 otherIndex，嘗試計算...`
                )

                // 嘗試從 survey 中獲取問題信息來計算 otherIndex
                const question = survey?.getQuestionByName?.(actualKey)
                if (question && question.showOtherItem) {
                  otherKey =
                    question.choices.length + (question.showNoneItem ? 1 : 0)

                  // 同時更新 otherAnswers
                  otherAnswers[actualKey] = { otherIndex: otherKey }
                  console.log(
                    `🔧 計算並設定 ${actualKey} 的 other 索引:`,
                    otherKey
                  )
                } else {
                  console.error(`❌ 無法計算 ${actualKey} 的 otherIndex`)
                  return acc // 跳過這個評論
                }
              }

              if (otherKey !== undefined) {
                correspondingObj.other[otherKey] = value
                console.log(
                  `✅ 成功設定評論 ${key} 到 other[${otherKey}]:`,
                  value
                )
              }
            } else {
              console.warn(`⚠️ 找不到對應的主問題物件 ${actualKey}`)
            }

            return acc
          }

          // 複選的 ans 與交卷一致，採顯示位置；other 文字的 key 仍採完整 choices 索引。
          const question = survey?.getQuestionByName?.(key)
          refreshMultipleChoiceOrder(question, choiceIndexMapRef, choicesOrderRef)
          const isMultiple = question?.getType() === 'checkbox' ||
            (question?.getType() === 'imagepicker' && question.multiSelect)
          const specialDisplayIndex = (value) => {
            if (!isMultiple || (value !== 'none' && value !== 'other')) return undefined
            const mapped = choiceIndexMapRef.current[key]?.[value]
            if (typeof mapped === 'number') return mapped
            if (typeof mapped?.index === 'number') return mapped.index
            const index = question.visibleChoices.findIndex((choice) => choice.value === value)
            return index >= 0 ? index : undefined
          }
          let ans = []

          // 檢查是否有對應的 choiceIndexMapRef
          if (choiceIndexMapRef.current[key]) {
            console.log(
              `📊 問題 ${key} 有 choiceIndexMapRef:`,
              choiceIndexMapRef.current[key]
            )

            if (Array.isArray(value)) {
              // 檢查是否有檔案
              if (value[0]?.content) {
                ans = ['[FILE_PENDING]'] // 標記檔案尚未處理
              } else {
                // 多選題：將每個答案值轉換為索引
                ans = value.map((v) => {
                  const displayIndex = specialDisplayIndex(v)
                  if (displayIndex !== undefined) return displayIndex
                  if (v === 'other') {
                    const otherIndex = otherAnswers[key]?.otherIndex
                    if (otherIndex !== undefined) {
                      return otherIndex
                    } else {
                      console.warn(`⚠️ 找不到 ${key} 的 otherIndex，使用原值`)
                      return v
                    }
                  } else if (v === 'none') {
                    const noneIndex = noneAnswers[key]?.noneIndex
                    if (noneIndex !== undefined) {
                      return noneIndex
                    } else {
                      console.warn(`⚠️ 找不到 ${key} 的 noneIndex，使用原值`)
                      return v
                    }
                  } else {
                    // 查找答案在 choiceIndexMapRef 中的索引
                    return typeof choiceIndexMapRef.current[key][v] === 'number'
                      ? choiceIndexMapRef.current[key][v]
                      : choiceIndexMapRef.current[key][v]?.index ?? v
                  }
                })
              }
            } else if (
              typeof value === 'object' &&
              value !== null &&
              !Array.isArray(value)
            ) {
              // 矩陣題的特殊處理
              console.log(`🎯 處理矩陣題 ${key}:`, value)

              if (matrixAnswers[key]) {
                ans = matrixAnswers[key]
                console.log(`✅ 使用已存在的矩陣答案:`, ans)
              } else {
                // 從 value 物件轉換為矩陣格式
                // [FIX] 使用 question.rows 遍歷所有行（含隱藏），確保隱藏子題補空陣列
                const question = survey?.getQuestionByName?.(key)
                const matrixValue =
                  question?.rows
                    ? question.rows.map((row) =>
                        value[row.value] !== undefined
                          ? [value[row.value]]
                          : []
                      )
                    : Object.values(value).map((v) => [v])
                ans = matrixValue
                matrixAnswers[key] = matrixValue // 同時更新 matrixAnswers
                console.log(`🔄 轉換矩陣答案 ${key}:`, matrixValue)
              }
            } else if (value === 'other') {
              const otherIndex = otherAnswers[key]?.otherIndex
              ans = [otherIndex !== undefined ? otherIndex : 0]
            } else if (value === 'none') {
              const noneIndex = noneAnswers[key]?.noneIndex
              ans = [noneIndex !== undefined ? noneIndex : 0]
            } else {
              // 單選題：查找答案在 choiceIndexMapRef 中的索引
              const answerIndex = choiceIndexMapRef.current[key][value]
              if (answerIndex?.title) {
                // 帶入帶出的情況
                ans = [answerIndex.index]
              } else if (typeof answerIndex === 'number') {
                ans = [answerIndex]
              } else {
                ans = [value] // 開放題或其他情況
              }
            }
          } else {
            // 沒有 choiceIndexMapRef 的情況（如開放題、矩陣題）
            console.log(
              `📝 問題 ${key} 沒有 choiceIndexMapRef，直接處理值:`,
              value
            )

            if (value === 'other') {
              const otherIndex = otherAnswers[key]?.otherIndex
              ans = [otherIndex !== undefined ? otherIndex : 0]
            } else if (value === 'none') {
              const noneIndex = noneAnswers[key]?.noneIndex
              ans = [noneIndex !== undefined ? noneIndex : 0]
            } else if (
              typeof value === 'object' &&
              value !== null &&
              !Array.isArray(value)
            ) {
              // 矩陣題處理
              console.log(
                `🎯 矩陣題 ${key} 無 choiceIndexMapRef，直接轉換:`,
                value
              )

              if (matrixAnswers[key]) {
                ans = matrixAnswers[key]
                console.log(`✅ 使用已存在的矩陣答案:`, ans)
              } else {
                // 從 surveyData 的矩陣值轉換
                // [FIX] 使用 question.rows 遍歷所有行（含隱藏），確保隱藏子題補空陣列
                const question = survey?.getQuestionByName?.(key)
                const matrixValue =
                  question?.rows
                    ? question.rows.map((row) =>
                        value[row.value] !== undefined
                          ? [value[row.value]]
                          : []
                      )
                    : Object.values(value).map((v) => [v])
                ans = matrixValue
                matrixAnswers[key] = matrixValue
                console.log(`🔄 新建矩陣答案 ${key}:`, matrixValue)
              }
            } else if (Array.isArray(value)) {
              // 檢查是否有檔案
              if (value[0]?.content) {
                ans = ['[FILE_PENDING]'] // 標記檔案尚未處理
              } else {
                ans = value.map((v) => {
                  const displayIndex = specialDisplayIndex(v)
                  if (displayIndex !== undefined) return displayIndex
                  if (v === 'other') {
                    const otherIndex = otherAnswers[key]?.otherIndex
                    return otherIndex !== undefined ? otherIndex : v
                  } else if (v === 'none') {
                    const noneIndex = noneAnswers[key]?.noneIndex
                    return noneIndex !== undefined ? noneIndex : v
                  } else {
                    return v
                  }
                })
              }
            } else {
              // 文字題或數字題
              ans = [value]
            }
          }

          // 帶入題的來源題號（整數 qi）。正常取 choiceIndexMapRef 內存的 origin_qi，
          // 這裡只是後備：從 bringOptionsMapRef（{ 來源題名: 目標題名 }）反查。
          // 曾直接送反查到的題名字串（如 "q3"），與交卷路徑、API 規格不符。
          const sourceName = findBringInSourceName(
            bringOptionsMapRef?.current,
            key
          )
          const fallbackOriginQi = sourceName
            ? Number(sourceName.slice(1))
            : undefined

          const questionItem = {
            pi: pid,
            ans: ans,
            ord: choicesOrderRef.current[key] || [],
            other: {}, // 這個會在處理 -Comment 時填入
            ct: timestamps[key] || Date.now(),
            qi: Number(key.slice(1)),
            ...(choiceIndexMapRef?.current[key]?.[0]?.title
              ? {
                  bringing_options: {
                    // 與交卷路徑（useSubmitAnswers finalQuestionItem）同形：
                    // 只留帶入 slot（含 title 的物件），濾掉目標題自己 valueChanged
                    // 寫入的純數字 value→index 對應，否則會多出沒有 na 的空選項
                    options: getBringInPayloadSlots(question, choiceIndexMapRef.current[key])
                      .map((v) => ({
                        o_type: 0,
                        na: v.title,
                        origin_op: v.origin_op,
                        origin_qi: Number.isInteger(v.origin_qi)
                          ? v.origin_qi
                          : fallbackOriginQi,
                      })),
                  },
                }
              : {}),
          }

          console.log(`📦 ${key} 轉換結果:`, questionItem)
          acc.push(questionItem)

          return acc
        },
        []
      )

      console.log('🎯 convertAnswersSimple 最終結果:', transformedData)
      return transformedData
    },
    [
      pid,
      choiceIndexMapRef,
      choicesOrderRef,
      bringOptionsMapRef,
      otherAnswers,
      noneAnswers,
      matrixAnswers,
      timestamps,
      survey,
    ]
  )

  // 建立進度儲存格式的資料
  const createProgressData = useCallback(
    (surveyData) => {
      try {
        const simpleTransformedAnswers = convertAnswersSimple(surveyData)
        const sortedAnswers = simpleTransformedAnswers.sort(
          (a, b) => a.qi - b.qi
        )

        // 建立與最終提交相同格式的資料
        const submitFormatData = {
          pid: pid,
          answers: sortedAnswers,
        }

        return submitFormatData
      } catch (error) {
        console.error('建立進度資料失敗:', error)
        throw error
      }
    },
    [pid, ac, tk, convertAnswersSimple]
  )

  // 暫存每次換頁都會送，同一條違規只通報一次，避免洗版
  const reportedViolationsRef = useRef(new Set())

  // 儲存進度 API 呼叫（預留）
  const saveProgressAPI = useCallback(
    async (progressData) => {
      recordProgressEvent(survey, 'save_started', progressData?.answers?.length)
      // 答案格式檢核（規則：src/utils/answerFormat.md）
      // fail-open：只打 Discord 通報，不擋暫存
      try {
        const newViolations = validateAnswerPayload(
          survey,
          progressData?.answers,
          pid
        ).filter((v) => !reportedViolationsRef.current.has(v))
        if (newViolations.length > 0) {
          newViolations.forEach((v) => reportedViolationsRef.current.add(v))
          sendDiscordNotification(
            formatViolationMessage(pid, newViolations, '暫存', {
              ac,
              title: survey?.title,
              diagnostics: buildViolationDiagnostics(
                survey,
                progressData?.answers,
                newViolations,
                {
                  choiceIndexMap: choiceIndexMapRef.current,
                  matrixAnswers,
                }
              ),
            })
          )
        }
      } catch (e) {
        console.error('暫存答案格式檢核執行失敗', e)
      }

      try {
        console.log('儲存', progressData)

        // TODO: 未來的 API 呼叫
        const response = await axios.post(host + SaveAnswersUrl, progressData, {
          headers: {
            'Content-Type': 'text/plain; charset=UTF-8',
            Accept: '*/*',
            ac: ac,
            tk: tk,
          },
          timeout: 30000,
        })
        console.log('儲存進度 API 回應:', response)

        if (response.data.code === '02000') {
          recordProgressEvent(survey, 'save_succeeded', progressData?.answers?.length)
          console.log('進度儲存成功:', response.data.msg)
          return response.data
        } else {
          throw new Error(
            `儲存失敗: ${response.data.msg} (${response.data.code})`
          )
        }
      } catch (error) {
        recordProgressEvent(survey, 'save_failed')
        console.error('儲存進度 API 失敗:', error)

        // 檢查是否為網路錯誤、timeout 或請求被中止
        const isNetworkError =
          error.code === 'ECONNABORTED' || // axios timeout
          error.code === 'ERR_CANCELED' || // 請求被取消
          error.message.includes('timeout') || // 一般 timeout 訊息
          error.message.includes('aborted') || // 請求被中止
          error.message.includes('canceled') || // 請求被取消
          error.message.includes('Network Error') || // 網路錯誤
          !error.response // 沒有伺服器回應

        // 特別檢查是否為用戶主動中止
        const isUserAborted =
          error.code === 'ERR_CANCELED' ||
          error.message.includes('aborted') ||
          error.message.includes('canceled')

        // 用戶主動中止請求的處理
        if (isUserAborted) {
          console.log('進度儲存請求被用戶中止')
          throw error // 拋出錯誤但不顯示 alert
        }

        // 網路錯誤或 timeout 的處理
        if (isNetworkError) {
          alert('網路連線異常或儲存逾時，問卷進度可能未能成功儲存')
          throw error
        }

        // 檢查特定的錯誤情況
        if (error.response && error.response.status) {
          const status = error.response.status
          const errorCode = error.response.data?.code

          if (status === 401 || errorCode === '04006') {
            // 401 Unauthorized 或 04006 Invalid access - token 過期
            alert('問卷儲存失敗：存取權限已過期，請重新整理頁面後再試一次')
          } else if (status === 500) {
            // 500 Internal Server Error
            alert('伺服器錯誤導致進度儲存失敗，請稍後再試')
          } else {
            // 其他 API 錯誤
            alert('問卷進度儲存失敗，請重新整理頁面後再試一次')
          }
        } else {
          // 其他未知錯誤
          alert('進度儲存時發生未知錯誤，請重新整理頁面後再試一次')
        }

        // 打 Discord 通知（排除用戶主動中止和純網路問題）
        if (!isUserAborted && !isNetworkError) {
          sendDiscordNotification(
            `EZC Web SaveAnswers Error \n Error: ${error} \n pid: ${pid} \n ac: ${ac} \n Response: ${error.response?.data?.msg} \n Response Code: ${error.response?.data?.code} \n Status: ${error.response?.status} \n Message: ${error.message}`
          )
        }

        throw error
      }
    },
    [
      ac,
      tk,
      pid,
      survey,
      sendDiscordNotification,
      choiceIndexMapRef,
      matrixAnswers,
    ]
  )

  return {
    createProgressData,
    saveProgressAPI,
  }
}
